import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { ActivatedRoute, ActivatedRouteSnapshot, NavigationEnd, Router, RouterModule } from '@angular/router';
import { filter, startWith } from 'rxjs';
import { SchemaMembershipStateService } from '@net3-multiplatform-client/libs';

interface Breadcrumb {
  label: string;
  url: string;
}

@Component({
  selector: 'lib-breadcrumb',
  standalone: true,
  imports: [RouterModule, CommonModule],
  templateUrl: './breadcrumb.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BreadcrumbComponent {
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private schemaState = inject(SchemaMembershipStateService);

  breadcrumbs = signal<Breadcrumb[]>([]);
  isVisible = signal<boolean>(true);
  title = computed(() => {
    const list = this.breadcrumbs();
    return list.length > 0 ? list[list.length - 1].label : '';
  });

  constructor() {
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      startWith(null)
    ).subscribe(() => {
      const { breadcrumbs, visible } = this.buildBreadcrumbs(this.activatedRoute.root);
      this.breadcrumbs.set(breadcrumbs);
      this.isVisible.set(visible);
    });
  }

  private buildBreadcrumbs(
    route: ActivatedRoute, 
    url: string = '', 
    breadcrumbs: Breadcrumb[] = [], 
    lastActive: boolean = true
  ): { breadcrumbs: Breadcrumb[], visible: boolean } {
    const children: ActivatedRoute[] = route.children;

    if (children.length === 0) {
      return { breadcrumbs, visible: lastActive };
    }

    for (const child of children) {
      if (child.snapshot == null) continue;
      const routeURL: string = child.snapshot.url.map(segment => segment.path).join('/');
      let nextUrl = url;
      if (routeURL !== '') {
        nextUrl += `/${routeURL}`;
      }

      const routeConfigData = child.snapshot.routeConfig?.data;
      // Strictly use routeConfig to avoid data inheritance
      const breadcrumbConfig = routeConfigData ? routeConfigData['breadcrumb'] : undefined;

      if (breadcrumbConfig) {
        let label: string | undefined;
        let isActiveConfig = true;

        // Resolve label
        if (typeof breadcrumbConfig === 'object' && breadcrumbConfig !== null && 'label' in breadcrumbConfig) {
          const l = breadcrumbConfig.label;
          if (typeof l === 'function') {
            label = l(child.snapshot.data);
          } else {
            label = l as string;
          }
        } else if (typeof breadcrumbConfig === 'function') {
          label = breadcrumbConfig(child.snapshot.data);
        } else {
          label = breadcrumbConfig as string;
        }

        // Resolve active state
        if (routeConfigData && 'active' in routeConfigData) {
          isActiveConfig = routeConfigData['active'] !== false;
        } else if (typeof breadcrumbConfig === 'object' && breadcrumbConfig !== null && 'active' in breadcrumbConfig) {
          isActiveConfig = breadcrumbConfig.active !== false;
        }

        if (label) {
          const lastBreadcrumb = breadcrumbs[breadcrumbs.length - 1];
          // Only add if it's the first one, or if the URL has changed, or if the label is different
          if (!lastBreadcrumb || (lastBreadcrumb.url !== nextUrl && lastBreadcrumb.label !== label)) {
            breadcrumbs.push({ label, url: nextUrl });
          } else if (lastBreadcrumb && lastBreadcrumb.url === nextUrl && lastBreadcrumb.label !== label) {
            // If URL is the same but label is different, update the label (child overrides parent)
            lastBreadcrumb.label = label;
          }
          
          // Update visibility state because this route defined a breadcrumb configuration
          lastActive = isActiveConfig;
        }
      }

      return this.buildBreadcrumbs(child, nextUrl, breadcrumbs, lastActive);
    }

    return { breadcrumbs, visible: lastActive };
  }
}
